/**
 * Service для загрузки файлов в S3 через backend API
 * Wrappers для upload endpoints
 */

import api from './api';

/**
 * Response types from upload API
 */
interface UploadResponse {
  success: boolean;
  data: {
    url: string;
    filename: string;
    size: number;
    mimetype: string;
  };
}

interface BatchUploadResponse {
  success: boolean;
  data: {
    files: Array<{
      url: string;
      filename: string;
      size: number;
      mimetype: string;
    }>;
    count: number;
  };
}

interface VideoUploadResponse {
  success: boolean;
  data: {
    url: string;
    filename: string;
    size: number;
    mimetype: string;
  };
}

interface DocumentUploadResponse {
  success: boolean;
  data: {
    url: string;
    filename: string;
    size: number;
    mimetype: string;
  };
}

/**
 * Загрузка одиночного изображения
 * 
 * @param file - File object для загрузки
 * @returns URL загруженного файла
 * @throws Error при неудачной загрузке
 */
export const uploadSingleImage = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('image', file);
  
  try {
    const response = await api.post<UploadResponse>('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    
    if (!response.data.success || !response.data.data.url) {
      throw new Error('Upload failed: no URL returned');
    }
    
    return response.data.data.url;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown upload error';
    throw new Error(`Image upload failed: ${message}`);
  }
};

/**
 * Загрузка множества изображений (до 10 файлов)
 * 
 * @param files - Array of File objects
 * @returns Array of URLs загруженных файлов
 * @throws Error при неудачной загрузке
 */
export const uploadBatchImages = async (files: File[]): Promise<string[]> => {
  if (files.length === 0) {
    return [];
  }
  
  if (files.length > 10) {
    throw new Error('Maximum 10 files allowed for batch upload');
  }
  
  const formData = new FormData();
  files.forEach(file => formData.append('images', file));
  
  try {
    const response = await api.post<BatchUploadResponse>('/upload/batch', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    
    if (!response.data.success || !response.data.data.files) {
      throw new Error('Batch upload failed: no files returned');
    }
    
    return response.data.data.files.map(f => f.url);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown upload error';
    throw new Error(`Batch upload failed: ${message}`);
  }
};

/**
 * Загрузка видеофайла (до 50MB)
 * 
 * @param file - Video File object
 * @returns URL загруженного видео
 * @throws Error при неудачной загрузке
 */
export const uploadVideo = async (file: File): Promise<string> => {
  // Client-side validation for video size (50MB max)
  const maxSize = 50 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error(`Video too large: ${(file.size / 1024 / 1024).toFixed(1)}MB. Maximum: 50MB`);
  }
  
  const formData = new FormData();
  formData.append('video', file);
  
  try {
    const response = await api.post<VideoUploadResponse>('/upload/video', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    
    if (!response.data.success || !response.data.data.url) {
      throw new Error('Video upload failed: no URL returned');
    }
    
    return response.data.data.url;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown upload error';
    throw new Error(`Video upload failed: ${message}`);
  }
};

/**
 * Загрузка документа (PDF, DOC, DOCX)
 * 
 * @param file - Document File object
 * @returns URL загруженного документа
 * @throws Error при неудачной загрузке
 */
export const uploadDocument = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('document', file);
  
  try {
    const response = await api.post<DocumentUploadResponse>('/upload/document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    
    if (!response.data.success || !response.data.data.url) {
      throw new Error('Document upload failed: no URL returned');
    }
    
    return response.data.data.url;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown upload error';
    throw new Error(`Document upload failed: ${message}`);
  }
};

/**
 * Загрузка газеты/журнала (PDF + optional cover image)
 * 
 * @param pdfFile - PDF File object
 * @param coverFile - Optional cover image File
 * @returns URLs загруженных файлов { pdfUrl, coverUrl }
 * @throws Error при неудачной загрузке
 */
export const uploadNewspaper = async (
  pdfFile: File,
  coverFile?: File
): Promise<{ pdfUrl: string; coverUrl?: string }> => {
  const formData = new FormData();
  formData.append('pdf', pdfFile);
  
  if (coverFile) {
    formData.append('cover', coverFile);
  }
  
  try {
    const response = await api.post<{
      success: boolean;
      data: {
        pdfUrl: string;
        coverUrl?: string;
        pdfFilename: string;
        pdfSize: number;
      };
    }>('/upload/newspaper', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    
    if (!response.data.success || !response.data.data.pdfUrl) {
      throw new Error('Newspaper upload failed: no PDF URL returned');
    }
    
    return {
      pdfUrl: response.data.data.pdfUrl,
      coverUrl: response.data.data.coverUrl,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown upload error';
    throw new Error(`Newspaper upload failed: ${message}`);
  }
};